import { useState } from 'react';
import { VolumeDiscount, VolumeDiscountCreate, VolumeDiscountUpdate } from '../types/costwand.d';
import { useVolumeDiscount } from '../hooks/useVolumeDiscount.ts';
import VolumeDiscountForm from '../components/VolumeDiscountForm.tsx';
import VolumeDiscountTable from '../components/VolumeDiscountTable.tsx';
import { Button } from '../components/ui/button.tsx';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
  // DialogFooter, // Removed DialogFooter as it's not used for the form anymore
} from '../components/ui/dialog.tsx';
// import { useError } from '../context/ErrorContext.tsx'; // Not needed as apiClient handles global error

const VolumeDiscountPage = () => {
  const {
    volumeDiscounts,
    loading,
    error,
    currentPage,
    totalPages,
    totalItems,
    handlePageChange,
    createVolumeDiscount,
    updateVolumeDiscount,
    deleteVolumeDiscount,
    fetchVolumeDiscounts,
  } = useVolumeDiscount();

  // const { displayError } = useError(); // Not needed as apiClient handles global error

  const [isFormOpen, setIsFormOpen] = useState<boolean>(false);
  const [editingVolumeDiscount, setEditingVolumeDiscount] = useState<VolumeDiscount | null>(null);
  const [formSubmitting, setFormSubmitting] = useState<boolean>(false);

  // Removed states for the separate error dialog for form submissions
  // const [isErrorDialogOpen, setIsErrorDialogOpen] = useState<boolean>(false);
  // const [errorDialogMessage, setErrorDialogMessage] = useState<string | null>(null);

  const handleAddVolumeDiscount = () => {
    setEditingVolumeDiscount(null);
    setIsFormOpen(true);
    // Removed clearing of local error messages
    // setErrorDialogMessage(null);
    // setIsErrorDialogOpen(false);
  };

  const handleEditVolumeDiscount = (volumeDiscount: VolumeDiscount) => {
    setEditingVolumeDiscount(volumeDiscount);
    setIsFormOpen(true);
    // Removed clearing of local error messages
    // setErrorDialogMessage(null);
    // setIsErrorDialogOpen(false);
  };

  const handleDeleteVolumeDiscount = async (id: number) => {
    await deleteVolumeDiscount(id);
    // The useVolumeDiscount hook already handles refetching after delete
  };

  const handleFormSubmit = async (data: VolumeDiscountCreate | VolumeDiscountUpdate) => {
    setFormSubmitting(true);
    // Removed clearing of local error messages
    // setErrorDialogMessage(null);
    // setIsErrorDialogOpen(false);

    try {
      let success = false;
      if (editingVolumeDiscount) {
        // For update, product_id is now included in the update payload
        const updateData: VolumeDiscountUpdate = {
          product_id: editingVolumeDiscount.product_id, // Ensure product_id is sent for updates
          min_quantity: data.min_quantity,
          price_retained_perc: data.price_retained_perc,
          updated_by: 'tester', // Assuming a default user for now
        };
        success = await updateVolumeDiscount(editingVolumeDiscount.id, updateData);
      } else {
        // For creation, product_id is required
        const createData: VolumeDiscountCreate = {
          product_id: (data as VolumeDiscountCreate).product_id, // Ensure product_id is present for creation
          min_quantity: data.min_quantity,
          price_retained_perc: data.price_retained_perc,
          created_by: 'tester', // Assuming a default user for now
        };
        success = await createVolumeDiscount(createData);
      }

      if (success) {
        setIsFormOpen(false);
        // The fetchVolumeDiscounts inside create/update will handle re-fetching and updating descriptions
      } else {
        // The global error handler in apiClient would have already displayed the error.
        // We just need to ensure the form stays open if it failed.
        // displayError(error || "An unexpected error occurred during form submission."); // If we wanted to explicitly trigger global error here
        // setIsErrorDialogOpen(true); // Removed local error dialog trigger
      }
    } catch (e: any) {
      // The global error handler in apiClient would have already displayed the error.
      // displayError(e.message || "An unexpected error occurred."); // If we wanted to explicitly trigger global error here
      // setIsErrorDialogOpen(true); // Removed local error dialog trigger
    } finally {
      setFormSubmitting(false);
    }
  };

  const handleFormCancel = () => {
    setIsFormOpen(false);
    setEditingVolumeDiscount(null);
    // Removed clearing of local error messages
    // setErrorDialogMessage(null);
    // setIsErrorDialogOpen(false);
  };

  // Removed handleCloseErrorDialog function

  return (
    <div className="p-4">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-2xl font-semibold text-secondary-foreground">Volume Discount Management</h2>
        <Dialog open={isFormOpen} onOpenChange={setIsFormOpen}>
          <DialogTrigger asChild>
            <Button onClick={handleAddVolumeDiscount}>Add New Volume Discount</Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[425px]">
            <DialogHeader>
              <DialogTitle>{editingVolumeDiscount ? 'Edit Volume Discount' : 'Add New Volume Discount'}</DialogTitle>
              <DialogDescription>
                {editingVolumeDiscount ? 'Make changes to the volume discount here.' : 'Add a new volume discount rule.'}
              </DialogDescription>
            </DialogHeader>
            <VolumeDiscountForm
              initialData={editingVolumeDiscount}
              onSubmit={handleFormSubmit}
              onCancel={handleFormCancel}
              isSubmitting={formSubmitting}
              // error={errorDialogMessage} // Removed the error prop
            />
          </DialogContent>
        </Dialog>
      </div>

      <VolumeDiscountTable
        volumeDiscounts={volumeDiscounts}
        currentPage={currentPage}
        totalPages={totalPages}
        totalItems={totalItems}
        onPageChange={handlePageChange}
        onEdit={handleEditVolumeDiscount}
        onDelete={handleDeleteVolumeDiscount}
        loading={loading}
        error={error}
        baseEndpoint="/discounts" // NEW: Pass the base endpoint
      />

      {/* Removed Error Dialog for form submission errors */}
      {/*
      <Dialog open={isErrorDialogOpen} onOpenChange={setIsErrorDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle className="text-destructive">Error</DialogTitle>
            <DialogDescription>
              An error occurred during the operation.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 text-red-600">
            {errorDialogMessage || 'An unexpected error occurred.'}
          </div>
          <DialogFooter>
            <Button onClick={handleCloseErrorDialog}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      */}
    </div>
  );
};

export default VolumeDiscountPage;